import React from "react";
import { Route, Routes } from "react-router-dom";
import { PublicClientApplication, } from "@azure/msal-browser";
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, } from "@azure/msal-react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { Config } from "./config/Config";
import "./grecaptcha-badge.css";

import { MainPage } from "./pages/MainPage";
import { AdministratorHomePage } from "./pages/AdministratorHomePage";
import { AuthorizationProvider } from "./contexts/AuthorizationContext";
import { GlobalProvider } from "./contexts/GlobalContext";
import { TenantProvider } from "./contexts/TenantContext";
import { FarmsPage } from "./pages/FarmsPage";
import { FieldsPage } from "./pages/FieldsPage";
import { UsersPage } from "./pages/UsersPage";
import { FieldPage } from "./pages/FieldPage";
import { MarketingHomePage } from "./pages/MarketingHomePage";
import { MarketingWeedPrediction } from "./pages/MarketingWeedPrediction";
import { MarketingHerbicideResistance } from "./pages/MarketingHerbicideResistance";
import { MarketingAboutUs } from "./pages/MarketingAboutUs";
import { MarketingContactUs } from "./pages/MarketingContactUs";
import { MarketingTestPage } from "./pages/MarketingTestPage";
import { ExampleFarmsPage } from "./pages/ExampleFarmsPage";
import { NewsMediaPage } from "./pages/NewsMediaPage";
import { ResearchPage } from "./pages/ResearchPage";
import { CompanyPage } from "./pages/CompanyPage";
import { AdministratorCompanyPage } from "./pages/AdministratorCompanyPage";
import { FarmPage } from "./pages/FarmPage";
import { UploadFieldFilesPage } from "./pages/UploadFieldFilesPage";
import { EditFilePage } from "./pages/EditFilePage";
import { UserProfilePage } from "./pages/UserProfilePage";
import { AdministratorFieldPage } from "./pages/AdminFieldPage";
import { NotAuthorizedPage } from "./pages/NotAuthorizedPage";
import { ScrollToTop } from "./components/ScrollToTop";
import { CmsLoginPage } from "./pages/cms/CmsLoginPage";
import { CmsCallbackPage } from "./pages/cms/CmsCallbackPage";
import { CmsDashboardPage } from "./pages/cms/CmsDashboardPage";

interface IProps {
	instance: PublicClientApplication;
}

export const App = ({ instance }: IProps): JSX.Element => {
	// Check if we're on a CMS route — these use Google OAuth, not MSAL.
	// Note: Using window.location instead of React Router's useLocation() because
	// App is rendered inside <BrowserRouter> but CMS routes must be checked before
	// wrapping in MsalProvider. This is intentional to avoid initializing MSAL on CMS pages.
	const isCmsRoute = window.location.pathname.startsWith("/cms");
	if (isCmsRoute) {
		return (
			<Routes>
				<Route path="/cms/login" element={<CmsLoginPage />} />
				<Route path="/cms/callback" element={<CmsCallbackPage />} />
				<Route path="/cms" element={<CmsDashboardPage />} />
			</Routes>
		);
	}

	return (
		<GoogleReCaptchaProvider reCaptchaKey={Config.recaptchaSiteKey}>
			<MsalProvider instance={instance}>
				<AuthenticatedTemplate>
					<GlobalProvider>
						<AuthorizationProvider>
							<TenantProvider>
							<Routes>
								<Route path="/" element={<MainPage />} />
								<Route path="/admin" element={<AdministratorHomePage />} />
								<Route path="/adminCompany" element={<AdministratorCompanyPage />} />
								<Route path="/adminField" element={<AdministratorFieldPage />} />
								<Route path="/company" element={<CompanyPage />} />
								<Route path="/farms" element={<FarmsPage />} />
								<Route path="/fields" element={<FieldsPage />} />
								<Route path="/field" element={<FieldPage />} />
								<Route path="/users" element={<UsersPage />} />
								<Route path="/upload" element={<UploadFieldFilesPage />} />
								<Route path="/farm" element={<FarmPage />} />
								<Route path="/editFile" element={<EditFilePage />} />
								<Route path="/profile" element={<UserProfilePage />} />
								<Route path="/notAuthorized" element={<NotAuthorizedPage />} />
							</Routes>
						</TenantProvider>
					</AuthorizationProvider>
				</GlobalProvider>
			</AuthenticatedTemplate>
			<UnauthenticatedTemplate>
				<ScrollToTop />
				<Routes>
					<Route path="/" element={<MarketingHomePage />} />
					<Route path="/example-farms" element={<ExampleFarmsPage />} />
					<Route path="/news" element={<NewsMediaPage />} />
					<Route path="/research" element={<ResearchPage />} />
					<Route path="/about-us" element={<MarketingAboutUs />} />
					<Route path="/contact-us" element={<MarketingContactUs />} />
					<Route path="/weed-prediction" element={<MarketingWeedPrediction />} />
					<Route path="/herbicide-resistance" element={<MarketingHerbicideResistance />} />
					<Route path="/test" element={<MarketingTestPage />} />
				</Routes>
			</UnauthenticatedTemplate>
		</MsalProvider>
		</GoogleReCaptchaProvider>
	);
};
